import { ApiError, NetworkError } from '@/shared/api/http'

/**
 * Коды бекенда человеческим языком — в одном месте на всё приложение.
 *
 * Зачем вообще переводить. Сообщения API написаны для разработчика:
 * «Wallet was added too recently» мерчант читает как поломку и идёт
 * в поддержку. Правильный ответ — «адрес добавлен слишком недавно,
 * вывод откроется через N часов»: человек понимает, что делать,
 * и никуда не пишет.
 *
 * Зачем в одном файле. Те же коды приходят на четырёх экранах.
 * Разложенные по экранам, переводы разъезжаются, и один и тот же
 * отказ объясняется по-разному в зависимости от того, откуда пришёл.
 */

function num(details: Record<string, unknown>, key: string): string | null {
  const value = details[key]
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string' && value.trim() !== '') return value
  return null
}

/** Склонение: «через 2 часа», «через 5 часов». */
function plural(count: string, one: string, few: string, many: string): string {
  const n = Number(count)
  if (!Number.isFinite(n)) return many
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/**
 * @param ticker тикер валюты, к которой относятся суммы в ответе.
 *   Бекенд кладёт в `details` голые числа — «available: 0.026448».
 *   Без единицы на шлюзе с BTC, XMR и USDT это число ни о чём
 *   не говорит: «Доступно: 0.026448» одинаково читается и как
 *   биткоины, и как доллары.
 */
export function explain(cause: unknown, ticker?: string): string {
  const unit = ticker ? ` ${ticker}` : ''
  if (cause instanceof NetworkError) return 'Сервер не отвечает'
  if (!(cause instanceof ApiError)) {
    return cause instanceof Error && cause.message ? cause.message : 'Что-то пошло не так'
  }

  const d = cause.details

  switch (cause.code) {
    // ---- вход ----
    case 'invalid_credentials':
    case 'login_failed':
      return 'Неверная почта или пароль'
    case 'invalid_code':
      return 'Неверный код'
    case 'code_expired':
      return 'Срок кода истёк'
    case 'token_expired':
    case 'invalid_token':
      return 'Сессия истекла'
    case 'wrong_password':
      return 'Текущий пароль введён неверно'
    case 'weak_password':
      return 'Пароль короче 8 символов'
    case 'forbidden':
      return 'Доступ закрыт'
    // ---- Telegram ----
    case 'telegram_not_linked':
      return 'Telegram не привязан к аккаунту'
    case 'telegram_login_disabled':
      return 'Вход через Telegram не подключён'
    case 'invalid_telegram_data':
    case 'telegram_data_expired':
      return 'Telegram не подтвердил вход'
    case 'telegram_taken':
      return 'Этот Telegram уже привязан к другому аккаунту'

    // ---- деньги ----
    case 'fee_changed': {
      const fee = num(d, 'fee')
      return fee
        ? `Комиссия сети изменилась: ${fee}${unit}`
        : 'Комиссия сети изменилась'
    }
    case 'fee_quote_unavailable':
      return 'Комиссия сети недоступна'
    case 'insufficient_funds': {
      const available = num(d, 'available')
      return available
        ? `Недостаточно средств. Доступно: ${available}${unit}`
        : 'Недостаточно средств на балансе'
    }
    case 'amount_below_minimum': {
      const minimum = num(d, 'minimum')
      return minimum
        ? `Сумма меньше минимальной: ${minimum}${unit}`
        : 'Сумма меньше минимальной для этой сети'
    }
    case 'daily_limit_exceeded': {
      const limit = num(d, 'limit')
      return limit
        ? `Превышен дневной лимит: ${limit}${unit}`
        : 'Превышен дневной лимит'
    }
    case 'wallet_too_new': {
      // Ограничение специально: перехваченный доступ иначе позволяет
      // добавить свой адрес и увести баланс тут же.
      const hours = num(d, 'required_hours')
      const left = num(d, 'minutes_left')
      if (left) return `Вывод на адрес откроется через ${left} мин.`
      if (hours) {
        return `Вывод на адрес откроется через ${hours} ${
          plural(hours, 'час', 'часа', 'часов')}`
      }
      return 'Вывод на этот адрес пока закрыт'
    }
    case 'wallet_not_found':
      return 'Кошелёк не найден'
    case 'wallet_in_use':
      return 'Кошелёк используется в автовыводе'
    case 'wallet_currency_mismatch':
      return 'Кошелёк другой валюты'
    case 'invalid_address':
      return 'Неверный адрес для этой сети'
    case 'withdrawal_currency_unsupported':
      return 'Вывод в этой сети сейчас недоступен'
    case 'withdrawal_not_cancelable':
    case 'withdrawal_leased':
      return 'Заявку уже нельзя отменить'
    case 'withdrawal_not_pending_approval':
      return 'Заявка уже обработана'

    // ---- повторы ----
    case 'idempotency_key_reused':
    case 'idempotency_conflict':
      return 'Такая заявка уже создана'

    case 'too_many_requests': {
      const wait = cause.retryAfter
      return wait
        ? `Слишком много попыток, подождите ${wait} с`
        : 'Слишком много попыток'
    }
    case 'validation_error':
      return 'Данные не приняты'
    case 'not_found':
      return 'Не найдено'

    default:
      return cause.message || 'Что-то пошло не так'
  }
}
